import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Typography, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/shared/lib/react';

interface MobileFullscreenCameraProps {
  open: boolean;
  onClose: () => void;
  stream: MediaStream | null;
  error: string | null;
  isInitializing: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  capturedImage: string | null;
  processing: boolean;
  onCapture: () => void;
  onRetake: () => void;
  onProcess: () => void;
  actionLabel: string;
  flashEnabled?: boolean;
  hasFlashSupport?: boolean;
  onToggleFlash?: () => void;
  facingMode?: 'user' | 'environment';
  hasMultipleCameras?: boolean;
  onSwitchCamera?: () => void;
  /**
   * Optional label rendered as a top-center header (e.g. "Tire 1 / 4").
   * Does not interfere with the close/flash/switch icon buttons.
   */
  headerLabel?: string;
}

export const MobileFullscreenCamera: React.FC<MobileFullscreenCameraProps> = ({
  open,
  onClose,
  stream,
  error,
  isInitializing,
  videoRef,
  canvasRef,
  capturedImage,
  processing,
  onCapture,
  onRetake,
  onProcess,
  actionLabel,
  flashEnabled = false,
  hasFlashSupport = false,
  onToggleFlash,
  facingMode = 'environment',
  hasMultipleCameras = false,
  onSwitchCamera,
  headerLabel,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);

  // Aria-labels below ('Capture photo', 'Close camera', 'Switch camera',
  // 'Turn on/off flash') are intentionally left as English literals: they
  // pre-date this audit, no other surface has translated screen-reader
  // copy yet, and i18n-ing aria-labels in isolation would make them the
  // only translated SR text in the app, creating an inconsistent SR
  // experience. Tracked as a follow-up in the project's i18n backlog.

  // Prevent body scroll when camera is open
  useEffect(() => {
    if (open) {
      const originalStyle = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [open]);

  // Handle mobile viewport height issues
  useEffect(() => {
    if (open && isMobile) {
      const updateHeight = () => {
        if (containerRef.current) {
          const vh = window.innerHeight * 0.01;
          containerRef.current.style.setProperty('--vh', `${vh}px`);
        }
      };

      updateHeight();
      window.addEventListener('resize', updateHeight);
      window.addEventListener('orientationchange', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
        window.removeEventListener('orientationchange', updateHeight);
      };
    }
  }, [open, isMobile]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) {
    return null;
  }

  const content = (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        bgcolor: 'black',
        height: isMobile ? 'calc(var(--vh, 1vh) * 100)' : '100vh',
        width: '100vw',
        touchAction: 'none',
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 50,
          bgcolor: 'rgba(0,0,0,0.5)',
          color: 'white',
          backdropFilter: 'blur(4px)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
        }}
        aria-label="Close camera"
      >
        <CloseIcon />
      </IconButton>

      {/* Flash Button */}
      {hasFlashSupport && onToggleFlash && !capturedImage && (
        <IconButton
          onClick={onToggleFlash}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 50,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            backdropFilter: 'blur(4px)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
          aria-label={flashEnabled ? 'Turn off flash' : 'Turn on flash'}
        >
          {flashEnabled ? <FlashlightOffIcon /> : <FlashlightOnIcon />}
        </IconButton>
      )}

      {/* Switch Camera Button */}
      {hasMultipleCameras && onSwitchCamera && !capturedImage && (
        <IconButton
          onClick={onSwitchCamera}
          sx={{
            position: 'absolute',
            top: 16,
            left: hasFlashSupport ? 72 : 16,
            zIndex: 50,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            backdropFilter: 'blur(4px)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
          aria-label="Switch camera"
        >
          <CameraswitchIcon />
        </IconButton>
      )}

      {/* Header Label */}
      {headerLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50,
            px: 2,
            py: 0.5,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            borderRadius: 999,
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {headerLabel}
          </Typography>
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 64,
            right: 64,
            zIndex: 50,
            p: 1.5,
            bgcolor: 'error.main',
            color: 'white',
            borderRadius: 2,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}

      {/* Video/Image Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!capturedImage ? (
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              bgcolor: 'black',
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
            }}
          />
        ) : (
          <Box
            component="img"
            src={capturedImage}
            alt="Captured"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              bgcolor: 'black',
            }}
          />
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {!capturedImage ? (
          <IconButton
            onClick={onCapture}
            disabled={!stream || processing || isInitializing}
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              color: 'white',
              backdropFilter: 'blur(4px)',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'rgba(0,0,0,0.3)', cursor: 'not-allowed' },
            }}
            aria-label="Capture photo"
          >
            <CameraAltIcon sx={{ fontSize: 28 }} />
          </IconButton>
        ) : (
          <>
            <Button
              onClick={onRetake}
              disabled={processing}
              variant="outlined"
              startIcon={<ReplayIcon />}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                backdropFilter: 'blur(4px)',
                px: 2,
                py: 1.5,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)', borderColor: 'rgba(255,255,255,0.5)' },
                '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
              }}
            >
              {t('common.camera.retake')}
            </Button>
            <Button
              onClick={onProcess}
              disabled={processing}
              variant="contained"
              color="success"
              startIcon={processing ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CheckIcon />}
              sx={{
                backdropFilter: 'blur(4px)',
                px: 2,
                py: 1.5,
                '&:disabled': { bgcolor: 'rgba(0,0,0,0.3)', cursor: 'not-allowed' },
              }}
            >
              {processing ? t('common.camera.processing') : actionLabel}
            </Button>
          </>
        )}
      </Box>

      {/* Loading State */}
      {isInitializing && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, color: 'white' }}>
            <CircularProgress sx={{ color: 'white' }} />
            <Typography>{t('common.camera.startingCamera')}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  return createPortal(content, document.body);
};
